import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import {
  MAX_DIMENSIONS,
  MAX_MEASURES,
  dimensionCell,
  measureFormatter,
  sharedDimensions,
  useAnalyticsQuery,
  useFormat,
  useWeekdayNames,
  type AnalyticsMeta,
  type MeasureMeta,
} from './analytics';

/**
 * C-11 — the ad-hoc report builder, driven entirely by `GET /analytics/meta`.
 *
 * The fixed panels on this screen name their own measures because they are
 * fixed *reports*. This one is the operator composing a selection, and its
 * option list is the server's registry rather than a copy of it: add a measure
 * to `MEASURES` and it appears here, delete one and it stops being offerable.
 * Nothing below contains a measure or dimension name.
 *
 * The pairing rules are the server's too. `/analytics/query` refuses a
 * dimension a measure's fact source cannot reach, so the dimension list is the
 * *intersection* over the chosen measures — an unreachable combination is
 * disabled rather than sent and 400'd.
 */
export function QueryBuilder({
  meta,
  filters,
  dateRange,
  currency,
  canExport,
  fileStem,
}: {
  /** Fetched once by the screen and shared, since the export list reads it too. */
  meta: { data: AnalyticsMeta | null; error: string | null; reload: () => void };
  filters: Record<string, string | undefined> | undefined;
  dateRange: { from: string; to: string };
  currency: string | null;
  canExport: boolean;
  fileStem: string;
}) {
  const { t } = useTranslation();
  const format = useFormat(currency);
  const weekday = useWeekdayNames();
  const renderMeasure = useMemo(() => measureFormatter(format), [format]);

  const [selectedMeasures, setSelectedMeasures] = useState<string[]>([]);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [limit, setLimit] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const catalogue: AnalyticsMeta | null = meta.data;

  // Seed once from whatever the catalogue actually publishes — the first
  // measure, and the row cap the server itself calls default. Naming a measure
  // here would reintroduce exactly the drift this component exists to remove.
  useEffect(() => {
    if (!catalogue) return;
    setSelectedMeasures((current) =>
      current.length
        ? current
        : catalogue.measures.filter((m) => m.permitted).slice(0, 1).map((m) => m.name),
    );
    setLimit((current) => current ?? catalogue.limits.defaultRows);
  }, [catalogue]);

  const measuresByName = useMemo(
    () => new Map((catalogue?.measures ?? []).map((m) => [m.name, m])),
    [catalogue],
  );

  const chosen = useMemo(
    () =>
      selectedMeasures.map((name) => measuresByName.get(name)).filter((m): m is MeasureMeta => !!m),
    [selectedMeasures, measuresByName],
  );

  const allowedDimensions = useMemo(() => sharedDimensions(chosen), [chosen]);

  // A dimension that survived a measure change is dropped rather than left
  // checked-but-ignored: the table must show what was actually asked for.
  useEffect(() => {
    setSelectedDimensions((current) => {
      const kept = current.filter((name) => allowedDimensions.includes(name));
      return kept.length === current.length ? current : kept;
    });
  }, [allowedDimensions]);

  const maxRows = catalogue?.limits.maxRows ?? 0;
  // Clamped before it is sent: the DTO's `Max` turns an over-cap limit into a
  // 400, and a typo in a number box should not read as a broken report.
  const effectiveLimit = catalogue
    ? Math.min(maxRows, Math.max(1, limit ?? catalogue.limits.defaultRows))
    : null;

  const body = useMemo(
    () =>
      chosen.length && effectiveLimit
        ? {
            measures: chosen.map((m) => m.name),
            ...(selectedDimensions.length ? { dimensions: selectedDimensions } : {}),
            filters,
            dateRange,
            limit: effectiveLimit,
          }
        : null,
    [chosen, selectedDimensions, filters, dateRange, effectiveLimit],
  );

  const result = useAnalyticsQuery(body);

  const toggleMeasure = (name: string) =>
    setSelectedMeasures((current) =>
      current.includes(name)
        ? current.filter((m) => m !== name)
        : current.length >= MAX_MEASURES
          ? current
          : [...current, name],
    );

  const toggleDimension = (name: string) =>
    setSelectedDimensions((current) =>
      current.includes(name)
        ? current.filter((d) => d !== name)
        : current.length >= MAX_DIMENSIONS
          ? current
          : [...current, name],
    );

  const exportCsv = async () => {
    if (!body) return;
    setExporting(true);
    try {
      const { data } = await api.post('/analytics/query/export', body, { responseType: 'blob' });
      const url = URL.createObjectURL(data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileStem}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const dimensionMeta = useMemo(
    () => new Map((catalogue?.dimensions ?? []).map((d) => [d.name, d])),
    [catalogue],
  );

  const rows = result.data?.rows ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle>{t('reports.builder.title')}</CardTitle>
          <CardDescription>
            {t('reports.builder.description')}
          </CardDescription>
        </div>
        {canExport && (
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || !body}>
            <Download className="h-4 w-4" aria-hidden />
            {t('reports.export')}
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        {meta.error ? (
          <ErrorState message={meta.error} onRetry={meta.reload} />
        ) : !catalogue ? (
          <LoadingState label={t('common.loading')} />
        ) : (
          <>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                {t('reports.builder.measures')}
              </legend>
              <p className="text-xs text-ink-muted">
                {t('reports.builder.measuresHint', {
                  max: MAX_MEASURES,
                })}
              </p>
              <div className="flex flex-wrap gap-2">
                {catalogue.measures.map((measure) => {
                  const on = selectedMeasures.includes(measure.name);
                  return (
                    <Chip
                      key={measure.name}
                      on={on}
                      // The description is the server's own words for the
                      // measure — the only place its meaning is defined. A
                      // measure this caller may not read says why instead:
                      // greyed out, not silently missing from the list.
                      title={
                        measure.permitted
                          ? measure.description
                          : t('reports.builder.notEntitled', {
                              permissions: measure.requires.join(', '),
                            })
                      }
                      disabled={
                        !measure.permitted ||
                        (!on && selectedMeasures.length >= MAX_MEASURES)
                      }
                      onClick={() => toggleMeasure(measure.name)}
                    >
                      {t(`reports.measures.${measure.name}`, measure.name)}
                    </Chip>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                {t('reports.builder.dimensions')}
              </legend>
              <p className="text-xs text-ink-muted">
                {t('reports.builder.dimensionsHint', {
                  max: MAX_DIMENSIONS,
                })}
              </p>
              <div className="flex flex-wrap gap-2">
                {catalogue.dimensions.map((dimension) => {
                  const on = selectedDimensions.includes(dimension.name);
                  const reachable = allowedDimensions.includes(dimension.name);
                  return (
                    <Chip
                      key={dimension.name}
                      on={on}
                      title={
                        !dimension.permitted
                          ? t('reports.builder.notEntitled', {
                              permissions: dimension.requires.join(', '),
                            })
                          : reachable
                            ? dimension.description
                            : t('reports.builder.unreachable')
                      }
                      disabled={
                        !dimension.permitted ||
                        !reachable ||
                        (!on && selectedDimensions.length >= MAX_DIMENSIONS)
                      }
                      onClick={() => toggleDimension(dimension.name)}
                    >
                      {t(`reports.dimensions.${dimension.name}`, dimension.name)}
                    </Chip>
                  );
                })}
              </div>
            </fieldset>

            <div className="w-32">
              <Field label={t('reports.builder.limit')}>
                <Input
                  type="number"
                  min={1}
                  max={maxRows}
                  value={limit ?? ''}
                  onChange={(event) =>
                    setLimit(event.target.value === '' ? null : Number(event.target.value))
                  }
                />
              </Field>
            </div>

            {!chosen.length ? (
              <EmptyState
                title={t('reports.builder.pickMeasure')}
                hint={t('reports.builder.pickMeasureHint')}
              />
            ) : result.loading ? (
              <LoadingState label={t('common.loading')} />
            ) : result.failed ? (
              <ErrorState
                message={t('reports.errors.load')}
                onRetry={result.reload}
              />
            ) : !rows.length ? (
              <EmptyState
                title={t('reports.empty.title')}
                hint={t('reports.empty.hint')}
              />
            ) : (
              <>
                <TableWrap className="border-0">
                  <Table>
                    <THead>
                      <TR>
                        {selectedDimensions.map((name) => (
                          <TH key={name}>{t(`reports.dimensions.${name}`, name)}</TH>
                        ))}
                        {chosen.map((measure) => (
                          <TH key={measure.name} className="text-end">
                            {t(`reports.measures.${measure.name}`, measure.name)}
                          </TH>
                        ))}
                      </TR>
                    </THead>
                    <tbody>
                      {rows.map((row, index) => (
                        <TR
                          // Dimension keys make a stable identity when they
                          // exist; a totals-only result is one row anyway.
                          key={
                            selectedDimensions.map((name) => String(row[name] ?? '')).join('|') ||
                            index
                          }
                        >
                          {selectedDimensions.map((name) => (
                            <TD key={name}>
                              {dimensionCell(row, dimensionMeta.get(name), format, weekday)}
                            </TD>
                          ))}
                          {chosen.map((measure) => (
                            <TD key={measure.name} className="text-end tabular-nums">
                              {renderMeasure(row[measure.name] ?? null, measure)}
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
                {result.data?.truncated && (
                  <p role="status" className="text-xs text-ink-muted">
                    {t('reports.builder.truncated', {
                      count: result.data.rowCount,
                    })}
                  </p>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** A toggle that reads as a filter chip. Disabled carries a reason via `title`. */
function Chip({
  on,
  disabled,
  title,
  onClick,
  children,
}: {
  on: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={
        on
          ? 'rounded-full bg-accent-solid px-3 py-1.5 text-sm text-accent-on'
          : 'rounded-full border px-3 py-1.5 text-sm disabled:opacity-40'
      }
    >
      {children}
    </button>
  );
}
